import { useTranslate } from '@tolgee/react'
import { getAllErrorDescriptions } from '@tom-ui/utils'
import { ErrorCode } from '@yard/utils/ErrorCode/error-code-enum'

const getStringPayload = (payload: unknown, key: string) => {
  if (!payload || typeof payload !== 'object') return undefined
  const value = (payload as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : undefined
}

const getNumberPayload = (payload: unknown, key: string) => {
  if (!payload || typeof payload !== 'object') return undefined
  const value = (payload as Record<string, unknown>)[key]
  return typeof value === 'number' ? value : undefined
}

const getBooleanPayload = (payload: unknown, key: string) => {
  if (!payload || typeof payload !== 'object') return undefined
  const value = (payload as Record<string, unknown>)[key]
  return typeof value === 'boolean' ? value : undefined
}

const formatImoClassLabel = (imoClass?: string) => {
  if (!imoClass) return 'Class unknown'
  return `Class ${imoClass}`
}

export const useExceptionMessageHandler = () => {
  const { t } = useTranslate()

  const getExceptionMessage = (error: any) => {
    const errorDescriptions = getAllErrorDescriptions(error)
    const errors: string[] = []

    errorDescriptions.forEach(errorDescription => {
      switch (errorDescription.code) {
        case ErrorCode.EntityNotFound:
          errors.push(
            t('entityNotFound', '{entity} with Id {id} not found', {
              entity: errorDescription.payload?.name,
              id: errorDescription.payload?.id,
            }),
          )
          break
        case ErrorCode.ValidationError:
          errors.push(
            t('propertyValidationError', '{property} has invalid value', {
              property: errorDescription.propertyName
                ? t(errorDescription.propertyName)
                : t('property', 'Property'),
            }),
          )
          break
        case ErrorCode.NameNotUnique:
          errors.push(
            t('specifiedFieldNotUnique', 'The value for {field} already exists', {
              field: errorDescription.payload.name,
            }),
          )
          break
        case ErrorCode.RowIndexOutOfBounds:
          errors.push(t('rowIndexOutOfBounds', 'Row index is out of bounds'))
          break
        case ErrorCode.BayIndexOutOfBounds:
          errors.push(t('bayIndexOutOfBounds', 'Bay index is out of bounds'))
          break
        case ErrorCode.SpaceOccupied:
          errors.push(t('spaceOccupied', 'Space is already occupied'))
          break
        case ErrorCode.ExceedingMaxBay:
          errors.push(t('exceedingMaxBay', 'Exceeding maximum bay limit'))
          break
        case ErrorCode.ExceedingMaxRow:
          errors.push(t('exceedingMaxRow', 'Exceeding maximum row limit'))
          break
        case ErrorCode.ExceedingMaxTier:
          errors.push(t('exceedingMaxTier', 'Exceeding maximum tier limit'))
          break
        case ErrorCode.ImdgStackingOnTopViolation:
          errors.push(
            t(
              'imdgStackingOnTopViolationError',
              'Stacking on top of {belowClass} is not allowed by the current DG stacking rules.',
              {
                belowClass: formatImoClassLabel(
                  getStringPayload(errorDescription.payload, 'belowImoClass'),
                ),
              },
            ),
          )
          break
        case ErrorCode.ImdgTierCapViolation:
          errors.push(
            t(
              'imdgTierCapViolationError',
              '{imoClass} may only be stacked up to tier {maxTiers}. Requested tier: {requestedTier}.',
              {
                imoClass: formatImoClassLabel(
                  getStringPayload(errorDescription.payload, 'imoClass'),
                ),
                maxTiers: getNumberPayload(errorDescription.payload, 'maxTiers') ?? 'unknown',
                requestedTier:
                  getNumberPayload(errorDescription.payload, 'requestedTier') ?? 'unknown',
              },
            ),
          )
          break
        case ErrorCode.ImdgSegregationViolation:
          if (getBooleanPayload(errorDescription.payload, 'isProhibited')) {
            errors.push(
              t(
                'imdgSegregationProhibitedError',
                '{imoClass} and {conflictingClass} cannot be placed in the same yard block under the current DG segregation rules.',
                {
                  imoClass: formatImoClassLabel(
                    getStringPayload(errorDescription.payload, 'imoClass'),
                  ),
                  conflictingClass: formatImoClassLabel(
                    getStringPayload(errorDescription.payload, 'conflictingImoClass'),
                  ),
                },
              ),
            )
            break
          }

          errors.push(
            t(
              'imdgSegregationViolationError',
              '{imoClass} must be separated from {conflictingClass} by at least {requiredDistance} ft. Actual distance: {actualDistance} ft.',
              {
                imoClass: formatImoClassLabel(
                  getStringPayload(errorDescription.payload, 'imoClass'),
                ),
                conflictingClass: formatImoClassLabel(
                  getStringPayload(errorDescription.payload, 'conflictingImoClass'),
                ),
                requiredDistance:
                  getNumberPayload(errorDescription.payload, 'requiredDistance') ?? 'unknown',
                actualDistance:
                  getNumberPayload(errorDescription.payload, 'actualBayDistance') ?? 'unknown',
              },
            ),
          )
          break
        default:
          break
      }
    })

    if (errors.length > 0) return errors.join('\n')

    return error?.response?.data?.detail ?? t('somethingWentWrong', 'Something went wrong')
  }

  return {
    getExceptionMessage,
  }
}
